import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useModal } from '../../context/ModalContext';

function EditorFieldsList({
  configuration,
  pdfFields,
  selectedFieldIndices,
  onSelectField,
  onDeleteFieldByName,
  onDeleteSelectedFields,
  onDuplicateField,
  currentPage
}) {
  const [groupByPage, setGroupByPage] = useState(true);
  const [collapsedPages, setCollapsedPages] = useState(new Set());
  const tableWrapperRef = useRef(null);

  // Auto-expand and scroll to current page group when currentPage changes
  useEffect(() => {
    if (!currentPage || !groupByPage) return;
    // Ensure the current page group is expanded
    setCollapsedPages(prev => {
      if (prev.has(currentPage)) {
        const next = new Set(prev);
        next.delete(currentPage);
        return next;
      }
      return prev;
    });
    // Scroll the page group header to the top of the fields list (below sticky thead)
    setTimeout(() => {
      const wrapper = tableWrapperRef.current;
      const row = wrapper?.querySelector(`[data-page-group="${currentPage}"]`);
      if (row && wrapper) {
        const wrapperRect = wrapper.getBoundingClientRect();
        const rowRect = row.getBoundingClientRect();
        // Account for sticky thead height so the page title appears just below it
        const thead = wrapper.querySelector('thead');
        const theadHeight = thead ? thead.getBoundingClientRect().height : 0;
        const scrollOffset = rowRect.top - wrapperRect.top + wrapper.scrollTop - theadHeight;
        wrapper.scrollTo({ top: Math.max(0, scrollOffset), behavior: 'smooth' });
      }
    }, 50);
  }, [currentPage, groupByPage]);
  const { showConfirm } = useModal();

  // Prefer the configuration ID because field names are not guaranteed to be unique.
  const fieldIndexMap = useMemo(() => {
    const byId = new Map();
    const byName = new Map();
    pdfFields.forEach((f, i) => {
      if (f.configId) byId.set(f.configId, i);
      byName.set(f.name, i);
    });
    return { byId, byName };
  }, [pdfFields]);

  // Sort by display order and filter visible
  const sortedFields = useMemo(() => {
    if (!configuration || !configuration.fields) return [];
    return [...configuration.fields]
      .filter(field => field.isVisible !== false)
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  }, [configuration]);

  // Group fields by page
  const fieldsByPage = useMemo(() => {
    if (!groupByPage || sortedFields.length === 0) return null;
    const groups = {};
    sortedFields.forEach(field => {
      const pdfField = pdfFields.find(pf => pf.configId === field.id)
        || pdfFields.find(pf => pf.name === field.pdfFieldName);
      const page = pdfField?.page || 1;
      if (!groups[page]) groups[page] = [];
      groups[page].push(field);
    });
    return groups;
  }, [sortedFields, pdfFields, groupByPage]);

  if (!configuration || !configuration.fields) {
    return (
      <div className="all-fields-list">
        <h3>All Fields</h3>
        <p style={{ textAlign: 'center', color: '#999', padding: '1rem' }}>No fields yet</p>
      </div>
    );
  }

  const handleDeleteField = async (e, fieldName, fieldId) => {
    e.stopPropagation();
    const ok = await showConfirm(`Are you sure you want to delete "${fieldName}"?`, { title: 'Delete Field', danger: true });
    if (ok) {
      await onDeleteFieldByName(fieldName, fieldId);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedFieldIndices.size === 0) return;
    const ok = await showConfirm(`Delete ${selectedFieldIndices.size} selected field(s)?`, { title: 'Delete Fields', danger: true });
    if (ok) {
      await onDeleteSelectedFields();
    }
  };

  const handleDuplicateField = (e, field) => {
    e.stopPropagation();
    if (onDuplicateField) {
      onDuplicateField(field);
    }
  };

  const togglePageCollapse = (page) => {
    setCollapsedPages(prev => {
      const next = new Set(prev);
      if (next.has(page)) next.delete(page);
      else next.add(page);
      return next;
    });
  };

  // Matches the canvas highlight palette in index.css — keep the two in sync.
  const badgeColors = {
    text: '#4a90e2',
    checkbox: '#28a745',
    radio: '#d97706',
    date: '#7e57c2',
    signature: '#dc3545',
    dropdown: '#64748b'
  };

  const renderFieldRow = (field, showConnector = false, isLastInGroup = false) => {
    const fieldIndex = fieldIndexMap.byId.get(field.id)
      ?? fieldIndexMap.byName.get(field.pdfFieldName)
      ?? -1;
    const isClickable = fieldIndex >= 0;
    const isSelected = selectedFieldIndices.has(fieldIndex);
    const displayName = field.pdfFieldName.length > 18
      ? field.pdfFieldName.substring(0, 18) + '...'
      : field.pdfFieldName;

    return (
      <tr
        key={field.id || field.pdfFieldName}
        style={{
          cursor: isClickable ? 'pointer' : 'default',
          opacity: isClickable ? 1 : 0.7,
          borderBottom: isLastInGroup ? '2px solid #e0e0e0' : '1px solid #f0f0f0',
          backgroundColor: isSelected ? '#e8f1f4' : 'transparent',
          transition: 'background-color 0.15s'
        }}
        title={isClickable ? 'Click to edit this field' : 'Field not found in PDF'}
        onClick={() => isClickable && onSelectField(fieldIndex)}
      >
        {/* Connector line column */}
        <td style={{ width: '16px', padding: '0', position: 'relative' }}>
          {showConnector && (
            <div style={{
              position: 'absolute',
              left: '7px',
              top: 0,
              bottom: isLastInGroup ? '50%' : 0,
              width: '2px',
              backgroundColor: badgeColors[field.displayType] || '#999'
            }} />
          )}
          {showConnector && (
            <div style={{
              position: 'absolute',
              left: '7px',
              top: '50%',
              width: '8px',
              height: '2px',
              backgroundColor: badgeColors[field.displayType] || '#999'
            }} />
          )}
        </td>
        <td style={{ padding: '0.35rem 0.2rem' }} title={field.pdfFieldName}>
          {displayName}
        </td>
        <td style={{ padding: '0.35rem 0.2rem' }}>
          <span style={{
            display: 'inline-block',
            padding: '1px 5px',
            borderRadius: '3px',
            fontSize: '0.7rem',
            color: 'white',
            background: badgeColors[field.displayType] || '#999'
          }}>
            {field.displayType}
          </span>
        </td>
        <td style={{ padding: '0.35rem 0.2rem', whiteSpace: 'nowrap' }}>
        </td>
      </tr>
    );
  };

  const renderPageGroup = (page, fields) => {
    const isCollapsed = collapsedPages.has(Number(page));
    const typeCount = {};
    fields.forEach(f => {
      typeCount[f.displayType] = (typeCount[f.displayType] || 0) + 1;
    });

    return (
      <React.Fragment key={`page-${page}`}>
        <tr
          data-page-group={page}
          style={{
            backgroundColor: '#f3f4f6',
            cursor: 'pointer',
            userSelect: 'none'
          }}
          onClick={() => togglePageCollapse(Number(page))}
        >
          <td colSpan="4" style={{ padding: '0.4rem 0.25rem', fontWeight: 600, fontSize: '0.8rem', color: '#374151' }}>
            <span style={{ marginRight: '6px', fontSize: '0.65rem', color: '#6b7280' }}>
              {isCollapsed ? '\u25B8' : '\u25BE'}
            </span>
            Page {page}
            <span style={{ fontWeight: 400, color: '#888', marginLeft: '8px', fontSize: '0.75rem' }}>
              ({fields.length} fields
              {Object.entries(typeCount).map(([type, count]) => (
                <span key={type} style={{
                  marginLeft: '4px',
                  padding: '0 3px',
                  borderRadius: '2px',
                  fontSize: '0.65rem',
                  color: 'white',
                  background: badgeColors[type] || '#999'
                }}>
                  {count}{type[0].toUpperCase()}
                </span>
              ))}
              )
            </span>
          </td>
        </tr>
        {!isCollapsed && fields.map((field, idx) =>
          renderFieldRow(field, true, idx === fields.length - 1)
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="all-fields-list">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
        <h3 style={{ margin: 0 }}>All Fields ({sortedFields.length})</h3>
        <label style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', color: '#666' }}>
          <input
            type="checkbox"
            checked={groupByPage}
            onChange={(e) => setGroupByPage(e.target.checked)}
            style={{ width: '14px', height: '14px', accentColor: 'var(--company-primary-color, #273947)' }}
          />
          Group by page
        </label>
      </div>

      {selectedFieldIndices.size > 0 && (
        <div style={{ display: 'flex', gap: '4px', marginBottom: '0.5rem' }}>
          <button
            className="btn btn-danger"
            onClick={handleDeleteSelected}
            style={{ flex: 1, fontSize: '0.8rem', padding: '4px 8px' }}
          >
            Delete Selected ({selectedFieldIndices.size})
          </button>
        </div>
      )}

      <div className="table-wrapper" ref={tableWrapperRef} style={{ maxHeight: '400px', overflowY: 'auto' }}>
        <table className="fields-table" style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ width: '16px' }}></th>
              <th style={{ textAlign: 'left', padding: '0.4rem 0.2rem' }}>Name</th>
              <th style={{ textAlign: 'left', padding: '0.4rem 0.2rem' }}>Type</th>
              <th style={{ width: '55px' }}></th>
            </tr>
          </thead>
          <tbody>
            {sortedFields.length === 0 ? (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', color: '#999', padding: '1rem' }}>
                  No fields yet
                </td>
              </tr>
            ) : groupByPage && fieldsByPage ? (
              Object.keys(fieldsByPage)
                .sort((a, b) => Number(a) - Number(b))
                .map(page => renderPageGroup(page, fieldsByPage[page]))
            ) : (
              sortedFields.map((field, idx) =>
                renderFieldRow(field, false, idx === sortedFields.length - 1)
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default React.memo(EditorFieldsList);
